import React from 'react';
import { Check, Copy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { UseFormReturn } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import {
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { FIELD_SECTIONS, type CatalogField, type CatalogProvider } from './catalog';
import type { FormValues } from './integrationHelpers';
import { SecretField } from './SecretField';

interface ProviderFieldsProps {
    form: UseFormReturn<FormValues>;
    provider: CatalogProvider;
    /** Stored (masked) configuration of the saved row. */
    saved?: Record<string, any> | null;
    disabled?: boolean;
    /** Shown under the credentials when the provider has a webhook secret. */
    webhookUrl?: string;
}

const optionLabel = (t: (key: string, options?: any) => string, value: string) =>
    t(`integrations.options.${value}`, { defaultValue: value });

/** Segmented control for the payment environment (sandbox / live). */
const Segmented: React.FC<{
    value: string;
    options: string[];
    onChange: (value: string) => void;
    disabled?: boolean;
    ariaLabel: string;
}> = ({ value, options, onChange, disabled, ariaLabel }) => {
    const { t } = useTranslation('crm');
    return (
        <div
            role="radiogroup"
            aria-label={ariaLabel}
            className="inline-flex rounded-md border border-border bg-muted p-0.5"
        >
            {options.map(option => {
                const selected = option === value;
                return (
                    <button
                        key={option}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        disabled={disabled}
                        onClick={() => onChange(option)}
                        className={cn(
                            'rounded px-4 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60',
                            selected
                                ? 'bg-card text-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        {optionLabel(t, option)}
                    </button>
                );
            })}
        </div>
    );
};

const WebhookUrl: React.FC<{ url: string }> = ({ url }) => {
    const { t } = useTranslation('crm');
    const [copied, setCopied] = React.useState(false);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
        } catch {
            setCopied(false);
        }
    };

    return (
        <div className="space-y-2">
            <p className="text-sm font-medium">{t('integrations.webhook.title')}</p>
            <div className="flex items-center gap-2">
                <Input readOnly value={url} dir="ltr" className="font-mono text-xs" />
                <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={copy}
                    aria-label={t('integrations.webhook.copy')}
                    title={t('integrations.webhook.copy')}
                >
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
            </div>
            <p className="text-sm text-muted-foreground">{t('integrations.webhook.help')}</p>
        </div>
    );
};

/**
 * The provider form, grouped into Environment / Credentials / Sender or Numbers / Options.
 * Labels and help come from the catalog; secrets render through `SecretField`.
 */
export const ProviderFields: React.FC<ProviderFieldsProps> = ({
    form,
    provider,
    saved,
    disabled,
    webhookUrl,
}) => {
    const { t } = useTranslation('crm');
    const hasWebhookSecret = provider.fields.some(field => field.key === 'webhook_secret');

    const renderField = (field: CatalogField) => (
        <FormField
            key={field.key}
            control={form.control}
            name={field.key}
            render={({ field: control }) => (
                <FormItem>
                    <FormLabel>
                        {t(field.label)}
                        {field.required && (
                            <span className="ms-1 text-destructive" aria-hidden="true">
                                *
                            </span>
                        )}
                    </FormLabel>
                    {field.secret ? (
                        <SecretField
                            key={String(saved?.[field.key] ?? 'none')}
                            name={control.name}
                            value={control.value ?? ''}
                            onChange={control.onChange}
                            onBlur={control.onBlur}
                            saved={saved?.[field.key] ? String(saved[field.key]) : null}
                            disabled={disabled}
                            placeholder={field.placeholder}
                        />
                    ) : field.type === 'select' && field.section === 'environment' ? (
                        <Segmented
                            value={control.value}
                            options={field.options ?? []}
                            onChange={control.onChange}
                            disabled={disabled}
                            ariaLabel={t(field.label)}
                        />
                    ) : field.type === 'select' ? (
                        <Select
                            value={control.value || undefined}
                            onValueChange={control.onChange}
                            disabled={disabled}
                        >
                            <FormControl>
                                <SelectTrigger>
                                    <SelectValue placeholder={t('integrations.form.select')} />
                                </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                                {(field.options ?? []).map(option => (
                                    <SelectItem key={option} value={option}>
                                        {optionLabel(t, option)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : (
                        <FormControl>
                            <Input
                                {...control}
                                value={control.value ?? ''}
                                type={
                                    field.type === 'email' || field.type === 'url'
                                        ? field.type
                                        : 'text'
                                }
                                inputMode={field.type === 'number' ? 'numeric' : undefined}
                                dir={
                                    field.type === 'email' || field.type === 'url'
                                        ? 'ltr'
                                        : undefined
                                }
                                disabled={disabled}
                                placeholder={field.placeholder}
                                autoComplete="off"
                            />
                        </FormControl>
                    )}
                    <FormDescription>{t(field.help)}</FormDescription>
                    <FormMessage />
                </FormItem>
            )}
        />
    );

    return (
        <div className="space-y-6">
            {FIELD_SECTIONS.map(section => {
                const fields = provider.fields.filter(field => field.section === section);
                if (fields.length === 0) return null;
                return (
                    <section key={section} className="space-y-4">
                        <h3 className="text-sm font-semibold text-foreground">
                            {t(`integrations.sections.${section}`)}
                        </h3>
                        {fields.map(renderField)}
                        {section === 'credentials' && hasWebhookSecret && webhookUrl && (
                            <WebhookUrl url={webhookUrl} />
                        )}
                    </section>
                );
            })}
        </div>
    );
};

export default ProviderFields;
